import React, { useState, useEffect, useMemo, useRef } from "react";
import axios from "axios";
import { Button, Input, Space, Table, message } from "antd";
import { SearchOutlined, ExpandAltOutlined } from "@ant-design/icons";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import Dialog from "@mui/material/Dialog";
import Slide from "@mui/material/Slide";
import AppBar from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import Highlighter from "react-highlight-words";
import RoleBasedTesterStatsDetail from "./roleBasedTesterStatsDetail";
import "./testerStatisticsV2.css";
import "./manualTestingStatsV2.css";

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const rolesApiUrl = process.env.REACT_APP_API_Roles;
const roleStatsBase = process.env.REACT_APP_API_RoleBasedOTPStats
  ? String(process.env.REACT_APP_API_RoleBasedOTPStats).replace(/\/$/, "")
  : "";
const roleTestersStatsUrl = roleStatsBase
  ? `${roleStatsBase}/role-testers-current-month-stats`
  : "";

const SYSTEM_ROLE_IDS = [1, 2, 3];
const ROLE_WISE_OTP_STATS_EXCLUDED_IDS = [4, 7, 9];

function filterTesterRoles(raw) {
  const list = Array.isArray(raw) ? raw : [];
  return list.filter(
    (r) =>
      r &&
      r.id &&
      !SYSTEM_ROLE_IDS.includes(Number(r.id)) &&
      !ROLE_WISE_OTP_STATS_EXCLUDED_IDS.includes(Number(r.id)) &&
      (r.is_tester === 1 || r.is_tester === true),
  );
}

function formatTesterName(record) {
  return [record.first_name, record.last_name].filter(Boolean).join(" ").trim() || "—";
}

/** Native `<select>` via wrapper so ESLint never treats it as the antd `Select` component. */
const NativeSelect = React.forwardRef((props, ref) => <select ref={ref} {...props} />);
NativeSelect.displayName = "NativeSelect";

const RoleBasedTesterStatisticsV2 = () => {
  const [messageApi, contextHolder] = message.useMessage();
  const [roles, setRoles] = useState([]);
  const [rolesLoaded, setRolesLoaded] = useState(false);
  const [selectedRoleId, setSelectedRoleId] = useState(null);
  const [testers, setTesters] = useState([]);
  const [searchText, setSearchText] = useState("");
  const [statsDialogOpen, setStatsDialogOpen] = useState(false);
  const [searchedColumn, setSearchedColumn] = useState("");
  const [selectedTester, setSelectedTester] = useState(null);
  const [loading, setLoading] = useState(false);
  const searchInput = useRef(null);

  const testerRoles = useMemo(() => filterTesterRoles(roles), [roles]);

  useEffect(() => {
    const loadRoles = async () => {
      if (!rolesApiUrl) {
        setRolesLoaded(true);
        return;
      }
      try {
        const res = await fetch(rolesApiUrl);
        if (!res.ok) throw new Error("Failed to load roles");
        const json = await res.json();
        const data = json.data ?? json.body ?? [];
        setRoles(Array.isArray(data) ? data : []);
      } catch (e) {
        console.error(e);
        setRoles([]);
      } finally {
        setRolesLoaded(true);
      }
    };
    loadRoles();
  }, []);

  useEffect(() => {
    if (!rolesLoaded || testerRoles.length === 0) return;
    setSelectedRoleId((prev) => {
      if (prev != null && testerRoles.some((r) => Number(r.id) === Number(prev))) {
        return prev;
      }
      return testerRoles[0].id;
    });
  }, [rolesLoaded, testerRoles]);

  const fetchTestersForRole = async (roleId) => {
    if (!roleTestersStatsUrl || !roleId) {
      setTesters([]);
      return;
    }
    setLoading(true);
    try {
      const response = await axios.post(roleTestersStatsUrl, {
        role_id: Number(roleId),
      });
      const payload = response.data;
      const list = Array.isArray(payload) ? payload : payload?.testers ?? [];
      const rows = Array.isArray(list) ? list : [];
      setTesters(
        rows.map((t) => ({
          ...t,
          display_name: formatTesterName(t),
        })),
      );
    } catch (error) {
      console.error("Error fetching role-based tester statistics:", error);
      messageApi.error(
        error.response?.data?.message ||
          error.message ||
          "Failed to load statistics for this role.",
      );
      setTesters([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!rolesLoaded || selectedRoleId == null) return;
    fetchTestersForRole(selectedRoleId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rolesLoaded, selectedRoleId]);

  const handleSearch = (selectedKeys, confirm, dataIndex) => {
    confirm();
    setSearchText(selectedKeys[0]);
    setSearchedColumn(dataIndex);
  };

  const handleReset = (clearFilters) => {
    clearFilters();
    setSearchText("");
  };

  const getColumnSearchProps = (dataIndex) => ({
    filterDropdown: ({
      setSelectedKeys,
      selectedKeys,
      confirm,
      clearFilters,
      close,
    }) => (
      <div className="tester-stats-v2-filter" onKeyDown={(e) => e.stopPropagation()}>
        <Input
          ref={searchInput}
          placeholder={`Search ${dataIndex}`}
          value={selectedKeys[0]}
          onChange={(e) =>
            setSelectedKeys(e.target.value ? [e.target.value] : [])
          }
          onPressEnter={() => handleSearch(selectedKeys, confirm, dataIndex)}
        />
        <Space className="tester-stats-v2-filter-actions">
          <Button
            type="primary"
            size="small"
            icon={<SearchOutlined />}
            onClick={() => handleSearch(selectedKeys, confirm, dataIndex)}
          >
            Search
          </Button>
          <Button size="small" onClick={() => clearFilters && handleReset(clearFilters)}>
            Reset
          </Button>
          <Button type="link" size="small" onClick={() => close()}>
            Close
          </Button>
        </Space>
      </div>
    ),
    filterIcon: (filtered) => (
      <SearchOutlined className={filtered ? "tester-stats-v2-filter-active" : ""} />
    ),
    onFilter: (value, record) =>
      (record[dataIndex]?.toString() || "").toLowerCase().includes(value.toLowerCase()),
    onFilterDropdownOpenChange: (visible) => {
      if (visible) setTimeout(() => searchInput.current?.select(), 100);
    },
    render: (text) =>
      searchedColumn === dataIndex ? (
        <Highlighter
          highlightStyle={{ backgroundColor: "rgba(56, 189, 248, 0.3)", padding: 0 }}
          searchWords={[searchText]}
          autoEscape
          textToHighlight={text != null ? text.toString() : ""}
        />
      ) : (
        text
      ),
  });

  const handleViewStats = (record) => {
    setSelectedTester(record);
    setStatsDialogOpen(true);
  };

  const columns = [
    {
      title: "Tester Name",
      dataIndex: "display_name",
      key: "display_name",
      width: "35%",
      ...getColumnSearchProps("display_name"),
      sorter: (a, b) => (a.display_name || "").localeCompare(b.display_name || ""),
      sortDirections: ["descend", "ascend"],
    },
    {
      title: "OTPs this month",
      dataIndex: "current_month_otps_count",
      key: "current_month_otps_count",
      width: "35%",
      ...getColumnSearchProps("current_month_otps_count"),
      sorter: (a, b) =>
        (a.current_month_otps_count || 0) - (b.current_month_otps_count || 0),
      sortDirections: ["descend", "ascend"],
      defaultSortOrder: "descend",
    },
    {
      title: "Actions",
      key: "actions",
      width: "30%",
      render: (_, record) => (
        <div className="tester-stats-v2-actions">
          <Button
            type="default"
            icon={<ExpandAltOutlined />}
            onClick={() => handleViewStats(record)}
            className="tester-stats-v2-btn-view"
          >
            View Details
          </Button>
        </div>
      ),
    },
  ];

  if (!rolesApiUrl) {
    return (
      <div className="tester-stats-v2">
        <div className="manual-stats-v2-empty" style={{ padding: 24 }}>
          REACT_APP_API_Roles is not configured.
        </div>
      </div>
    );
  }

  if (!roleTestersStatsUrl) {
    return (
      <div className="tester-stats-v2">
        <div className="manual-stats-v2-empty" style={{ padding: 24 }}>
          REACT_APP_API_RoleBasedOTPStats is not configured.
        </div>
      </div>
    );
  }

  if (rolesLoaded && testerRoles.length === 0) {
    return (
      <div className="tester-stats-v2">
        <div className="tester-stats-v2-header">
          <h1 className="tester-stats-v2-title">Testers by Role: Current Month Stats</h1>
        </div>
        <div className="manual-stats-v2-empty" style={{ padding: 24 }}>
          No tester roles found. Create a role with tester enabled in Roles Manager.
        </div>
      </div>
    );
  }

  return (
    <div className="tester-stats-v2">
      {contextHolder}
      <div className="tester-stats-v2-header tester-stats-v2-header--role-filter">
        <h1 className="tester-stats-v2-title">Testers by Role: Current Month Stats</h1>
        <div className="manual-stats-v2-role-bar role-based-tester-stats-role-bar">
          <span className="manual-stats-v2-role-label">Role</span>
          <NativeSelect
            className="manual-stats-v2-role-select"
            disabled={!rolesLoaded}
            value={selectedRoleId != null ? String(selectedRoleId) : ""}
            onChange={(e) => {
              const v = e.target.value;
              setSelectedRoleId(v ? Number(v) : null);
            }}
            aria-label="Select role"
          >
            {!rolesLoaded ? (
              <option value="">Loading roles…</option>
            ) : (
              testerRoles.map((r) => (
                <option key={r.id} value={r.id}>
                  {r.title || `Role ${r.id}`}
                </option>
              ))
            )}
          </NativeSelect>
        </div>
      </div>

      <div className="tester-stats-v2-card">
        <Table
          columns={columns}
          dataSource={testers}
          rowKey="id"
          loading={loading || !rolesLoaded || selectedRoleId == null}
          locale={{
            emptyText:
              loading || !rolesLoaded ? "Loading..." : "No testers for this role.",
          }}
          pagination={{
            showSizeChanger: true,
            defaultPageSize: 10,
            pageSizeOptions: ["5", "10", "20", "50"],
            showTotal: (total) => `Total ${total} testers`,
          }}
          className="tester-stats-v2-table"
          scroll={{ x: "max-content" }}
        />
      </div>

      <Dialog
        open={statsDialogOpen}
        onClose={() => setStatsDialogOpen(false)}
        TransitionComponent={Transition}
        maxWidth="md"
        fullWidth
        className="tester-stats-v2-dialog"
        PaperProps={{
          sx: {
            borderRadius: 8,
            boxShadow: "0 24px 48px rgba(0, 0, 0, 0.12)",
            overflow: "hidden",
            maxHeight: "90vh",
          },
        }}
      >
        <AppBar className="tester-stats-v2-appbar" position="relative">
          <Toolbar className="tester-stats-v2-toolbar">
            <Typography variant="h6" component="div" className="tester-stats-v2-dialog-title">
              Tester statistics (day-wise)
            </Typography>
            {selectedTester && (
              <Typography variant="body2" className="tester-stats-v2-tester-name">
                {formatTesterName(selectedTester)}
              </Typography>
            )}
            <IconButton
              edge="end"
              color="inherit"
              onClick={() => setStatsDialogOpen(false)}
              aria-label="close"
            >
              <CloseIcon />
            </IconButton>
          </Toolbar>
        </AppBar>
        <div className="tester-stats-v2-body">
          {selectedTester && <RoleBasedTesterStatsDetail testerId={selectedTester.id} />}
        </div>
      </Dialog>
    </div>
  );
};

export default RoleBasedTesterStatisticsV2;
